import { ID } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { context } from '@adornis/baseql/server/context.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import {
  readEntitiesByIDs,
  watchEntitiesByIDs,
  readAllEntities,
  watchAllEntities,
} from '@adornis/bh-util/server/entity-reads.js';
import { mongo } from '@adornis/bh-util/server/mongo.js';
import { scopedCollection } from '@adornis/bh-util/server/scoped-collection.js';
import { getCurrentUserServer } from '@adornis/bh-util/util.js';
import { map, of } from 'rxjs';
import { Timeframe } from '../entities/Timeframe.js';

export const getTimeframesByIDs = registerQuery({
  operationName: 'getTimeframesByIDs',
  type: () => [Timeframe],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Timeframe>>(gqlFields: SelSet) =>
      readEntitiesByIDs(Timeframe, ids, gqlFields),
});

export const subscribeTimeframesByIDs = registerSubscription({
  operationName: 'subscribeTimeframesByIDs',
  type: () => [Timeframe],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Timeframe>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(Timeframe, ids, gqlFields),
});

export const getAllTimeframes = registerQuery({
  operationName: 'getAllTimeframes',
  type: () => [Timeframe],
  params: [],
  resolve:
    () =>
    <SelSet extends BaseQLSelectionSet<Timeframe>>(gqlFields: SelSet) =>
      readAllEntities(Timeframe, gqlFields),
});

export const subscribeAllTimeframes = registerSubscription({
  operationName: 'subscribeAllTimeframes',
  type: () => [Timeframe],
  params: [],
  resolve:
    () =>
    <SelSet extends BaseQLSelectionSet<Timeframe>>(gqlFields: SelSet) =>
      watchAllEntities(Timeframe, gqlFields),
});

export const getTimeframeByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<Timeframe>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getTimeframesByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeTimeframeByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<Timeframe>>(gqlFields: SelSet) =>
    id ? subscribeTimeframesByIDs([id])(gqlFields).pipe(map(timeframes => timeframes[0] ?? null)) : of(null);

const registerGetActiveTimeframesByInstitutionID = registerQuery({
  operationName: 'getActiveTimeframesByInstitutionID',
  resolve:
    (institutionID: string) =>
    async <SelSet extends BaseQLSelectionSet<Timeframe>>(
      gqlFields: SelSet,
    ): Promise<NarrowedEntity<Timeframe, SelSet>[]> => {
      const collection = await scopedCollection(Timeframe);
      const result = await collection.find<Timeframe>({ institutionID, isActive: true }).toArray();
      return result as unknown as NarrowedEntity<Timeframe, SelSet>[];
    },
  type: () => [Timeframe],
  params: [{ name: 'institutionID', type: () => ID }],
});

const activateTimeframe = registerMutation({
  operationName: 'activateTimeframe',
  resolve: (timeframeID: string) => async () => {
    const collection = await scopedCollection(Timeframe._class);
    const timeframe = await collection.findOne<Timeframe>({ _id: timeframeID });
    if (!timeframe) throw new Error(`Timeframe with ID ${timeframeID} not found`);

    if (!context.serverContext) {
      const contextUser = context.user;
      const user = typeof contextUser === 'function' ? await contextUser() : await getCurrentUserServer();
      const isMatchingAdmin = user?.hasPermission('CREATE_EDIT_DELETE_TIMEFRAMES');
      const isBuchungsheldAdmin = user?.hasPermission('edit-admin-institution-timeframes', timeframe.institutionID);
      if (!isMatchingAdmin && !isBuchungsheldAdmin) throw new Error('No permission to activate timeframes');
    }

    timeframe.isActive = true;
    const updateResult = await collection.updateOne({ _id: timeframeID }, { $set: { isActive: true } });
    if (updateResult.modifiedCount) {
      const rawCollection = mongo().collection(Timeframe._collectionName);
      await rawCollection.updateMany({ _id: { $ne: timeframeID as any } }, { $set: { isActive: false } });
      return true;
    }
    return false;
  },
  type: () => Boolean,
  params: [{ name: 'timeframe', type: () => ID }],
});

export namespace TimeframeAPI {
  export const getActiveTimeframesByInstitution = registerGetActiveTimeframesByInstitutionID;
  export const activate = activateTimeframe;
}

export const getTimeframesByInstitution = registerQuery({
  operationName: 'getTimeframesByInstitution',
  type: () => [Timeframe],
  params: [{ name: 'institutionID', type: () => String }],
  resolve: (institutionID: string) => {
    return async (gqlFields: BaseQLSelectionSet<Timeframe>) => {
      const collection = await scopedCollection<Timeframe>(Timeframe._class);
      const result = await collection.find<Timeframe>({ institutionID }).toArray();
      return result;
    };
  },
});

export const subscribeTimeframesByInstitution = registerSubscription({
  operationName: 'subscribeTimeframesByInstitution',
  type: () => [Timeframe],
  params: [
    { name: 'institutionID', type: () => String },
    { name: 'exceptTimeframeID', type: () => ID },
  ],
  resolve: (institutionID: string, exceptTimeFrameID?: string) => {
    return (gqlFields: BaseQLSelectionSet<Timeframe>) => {
      return watchQuery<Timeframe>(Timeframe._collectionName, {
        institutionID,
        ...(exceptTimeFrameID ? { _id: { $ne: exceptTimeFrameID } } : {}),
      });
    };
  },
});
